import React, { useCallback, useEffect, useRef } from 'react';

import isNumber from 'lodash/isNumber';

import { axisBottom, axisLeft } from 'd3-axis';
import { select } from 'd3-selection';

function wrap(allText) {
  // Below - eslint rule disabled so that select(this) can work
  // eslint-disable-next-line func-names
  allText.each(function() {
    // @ts-ignore - Needed for selecting the correct DOM element
    const text = select(this);

    // Assumes we're splitting on new line characters
    const words = text.text().split('\n').reverse();

    let word;
    let lineNumber = 0;

    const lineHeight = 1.1; // ems
    const y = text.attr('y');
    const dy = parseFloat(text.attr('dy'));

    // Clear existing Text
    text.text(null);

    // Initialize the loop
    word = words.pop();
    while (word) {
      lineNumber += 1;

      // Append the current word
      text.append('tspan')
        .attr('x', 0)
        .attr('y', lineNumber * lineHeight + y)
        .attr('dy', `${lineNumber * lineHeight + dy}em`)
        .text(word);

      word = words.pop();
    }
  });
}

const Axis = ({
  id,
  scale, 
  orient, 
  ticks,
  transform,
  tickFormat,
  tickValues,
  tickSize,
  tickSizeOuter,
  clipPath,
  axisLabel,
  height,
  width,
  margins,
  useTickWrapping = false,
  labelRotation = -15, // Only used for x axis
  fontSize = 12,
}) => {
  const axisRef = useRef();

  const renderAxis = useCallback(() => {
    let axis;

    if (orient === 'bottom') {
      axis = axisBottom(scale);
    }
    if (orient === 'left') {
      axis = axisLeft(scale);
    }
    if (ticks) {
      axis.ticks(ticks);
    } else if (tickValues && tickValues?.length) {
      axis.tickValues(tickValues);
    }
    if (tickFormat) {
      axis.tickFormat(tickFormat);
    }
    if (tickSize) {
      axis.tickSize(tickSize);
    }
    if (tickSizeOuter) {
      axis.tickSizeOuter(tickSizeOuter)
    }

    select(axisRef.current).call(axis)

    if (orient === 'bottom' && labelRotation !== 0 && isNumber(labelRotation)) {
      // Adjusts Bottom labels to reduce overlap
      select(axisRef.current)
        .selectAll('text')
        .style('text-anchor', 'end')
        .attr('dx', '-.8em')
        .attr('dy', '.15em')
        .attr('transform', `rotate(${labelRotation})`);
    }

    if (useTickWrapping) {
      select(axisRef.current)
        .selectAll('.tick text')
        .call(wrap, scale.bandwidth || 0)
        .style('width', scale.bandwidth || 0)
        .style('max-width', scale.bandwidth || 0)
        .style('word-wrap', 'break-word')
        .style('text-align', 'center')
    }

    select(axisRef.current)
      .selectAll('.tick text')
      .style('font-size', 12)
      .style('font-style', 'normal')
      .style('font-family', '"Helvetica", sans-serif')
      .style('color', '#676767')
      .style('line-height', 1.2)

    select(axisRef.current)
      .selectAll('.tick text tspan')
      .style('font-size', 12)
      .style('font-style', 'normal')
      .style('font-family', '"Helvetica", sans-serif')
      .style('color', '#676767')
      .style('line-height', 1.2)
  }, [labelRotation, orient, scale, tickFormat, tickSize, tickSizeOuter, tickValues, ticks, useTickWrapping])

  // Initial Render
  useEffect(() => {
    renderAxis();
  }, [renderAxis])

  const clipPathObj = clipPath ? { clipPath } : {};
  const transformObj = transform ? { transform } : {};

  const axisLabelStyleObj = orient === 'bottom' ? {
    id: `${id}-label`,
    textAnchor: 'end',
    x: Math.round(width / 2) || 0,
    y: height + margins.bottom * 0.80,
  } : {
    id: `${id}-label`,
    y: -margins.left || 0,
    x: Math.round(-height / 2) || 0,
    dy: '.75em',
    transform: 'rotate(-90)',
    textAnchor: 'end',
  }

  return (
    <g>
      <g
        id={id}
        ref={axisRef}
        className={`${orient}-axis`}
        fontSize={fontSize}
        {...clipPathObj}
        {...transformObj}
      />
      {
        axisLabel?.length && (
          <text {...axisLabelStyleObj}>
            {axisLabel}
          </text>
        )
      }
    </g>
  );
}

export default Axis;
